import { useFormik } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import * as Yup from "yup";
import {
  useGetReportAllFieldsQuery,
  useLazyGetReportsByCatTypeQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import {
  StandardReportFieldResponse,
  TReportWithFields,
} from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFieldInput } from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import DualListbox from "../../../components/dualListbox";
import { TCustomizableItemList } from "../../../components/types";
import { APP_CONFIG, REPORT_TYPES } from "../../../constants/constants";
import { useCreateCustomReportContext } from "../context/createCustomReport.context";
import {
  TModifyCustomReportCallbackProps,
  useReportUtils,
} from "../hooks/reportUtils";
import { createCustomReportInitialValues } from "./validation";

type TModalCreateCustomReportsStep2 = {
  modifyReport?: TReportWithFields;
  isCopyModify?: boolean;
};

const ModalCreateCustomReportsStep2: FC<TModalCreateCustomReportsStep2> = ({
  modifyReport,
  isCopyModify,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.createCustomReport.step2",
  });
  const { t: tFormFields } = useTranslation("translation", {
    keyPrefix: "reports.createCustomReport.formFields",
  });
  const navigate = useNavigate();

  // focus on first input field on mount
  const firstInputRef = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const {
    setReportName,
    selectedReportId,
    selectedFields,
    setSelectedFields,
    resetCreateCustomReportForms,
    specificReportResponse,
  } = useCreateCustomReportContext();

  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  // Get all fields for the standard/custom report
  const standaredReportFieldsQueryParams = {
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    search: "",
    report: selectedReportId || 0,
    is_custom: true,
  };
  const { data: dataReportAllFields, isFetching: isFetchingReportAllFields } =
    useGetReportAllFieldsQuery(standaredReportFieldsQueryParams, {
      skip: standaredReportFieldsQueryParams.report === 0,
    });

  const { createModifyCustomReportWithFilters } = useReportUtils();

  const formik = useFormik({
    initialValues: createCustomReportInitialValues,
    enableReinitialize: true,
    validateOnChange: false, // validation is debounced via debouncedValidateForm
    validationSchema: Yup.object().shape({
      report_name: Yup.string()
        .required("Please enter a name for the report")
        .max(255, "Maximum 255 characters")
        .test(
          "Unique Report Name", // happy path
          t("unique_name_error"), // error message
          (value) => {
            if (modifyReport && !isCopyModify) {
              return true; // don't check unique for modify report
            }
            return new Promise((resolve, reject) => {
              if (value === lastChangedReportName) {
                resolve(lastReportNameWasValid);
                return;
              }
              getReportsByCatTypeAPITrigger({
                type: REPORT_TYPES.CUSTOM, // check only custom reports for unique name
                name: value, // values.report_name,
                page: 1,
                page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
              })
                .unwrap()
                .then((response) => {
                  setLastChangedReportName(value);
                  if (response?.results?.length > 0) {
                    resolve(false);
                    setLastReportNameWasValid(false);
                  } else {
                    resolve(true);
                    setLastReportNameWasValid(true);
                  }
                })
                .catch((error) => {
                  console.error(
                    "Error fetching reports by category type:",
                    error
                  );
                });
            });
          }
        ),
      fields: Yup.array().min(1, "Please add at least one field"),
    }),
    initialTouched: {
      report_name: true,
    },
    onSubmit: (values, { setSubmitting }) => {
      // save values to context
      setReportName(values.report_name);
      setSelectedFields(values.fields);

      if (modifyReport && !isCopyModify) {
        // modification of existing report

        // update the report with new filters
        createModifyCustomReportWithFilters({
          modifyReportId: modifyReport.id,
          reportName: values.report_name,
          selectedFields: values.fields.map((item) => {
            const this_report_field =
              specificReportResponse?.report_fields?.find(
                (field) => Number(field.field.all_field_id) === Number(item.id)
              );
            return {
              field: Number(item.id),
              field_title: item.title,
              // retain aggregation when updating report
              ...(!!this_report_field && this_report_field.aggregate_function
                ? {
                    aggregate_function: this_report_field.aggregate_function,
                    column_type: this_report_field.column_type,
                    value: this_report_field.value,
                    operator: this_report_field.operator,
                  }
                : {}),
              // retain group by when updating report
              ...(!!this_report_field && this_report_field.is_group_by
                ? {
                    is_group_by: this_report_field.is_group_by,
                    group_order: this_report_field.group_order,
                  }
                : {}),
            };
          }),

          // filterValues: [],
          reportType: REPORT_TYPES.CUSTOM, // when editing a report it should always be `REPORT_TYPES.CUSTOM` // REPORT_TYPES.NEW_CUSTOM
          category: modifyReport.category,
          reportId: modifyReport.linked_standard_report || 0,
          navigate,
          finallyCallback: () => {
            // hide modal
            hideModal();
          },
        } as TModifyCustomReportCallbackProps);
      } else {
        // creation of new report

        // show next modal
        dispatch(
          setModalsData({
            ...modalsState,
            showModalNewCustomReportStep1: false,
            showModalNewCustomReportStep2: false,
            showModalNewCustomReportStep3: true,
          })
        );
      }
    },
  });

  // to prevent unnecessary API calls when debounced validateForm runs
  const [lastChangedReportName, setLastChangedReportName] = useState("");
  const [lastReportNameWasValid, setLastReportNameWasValid] = useState(false);

  // debounced form validation
  const debouncedValidateForm = useDebouncedCallback(formik.validateForm, 500);
  useEffect(() => {
    debouncedValidateForm();
  }, [formik.validateForm, formik.values]);

  //formik
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
    resetForm,
    submitForm,
    setFieldError,
    validateForm,
  } = formik;

  const [
    getReportsByCatTypeAPITrigger,
    { isFetching: isFetchingReportsByCatType },
  ] = useLazyGetReportsByCatTypeQuery();

  const [modifyReportDataReady, setModifyReportDataReady] =
    useState(!modifyReport);
  const [rightListData, setRightListData] = useState<TCustomizableItemList[]>(
    []
  );

  // prefill form fields
  useEffect(() => {
    if (!!modifyReport && !!dataReportAllFields && !isFetchingReportAllFields) {
      setFieldValue("report_name", isCopyModify ? "" : modifyReport.name);
      setLastChangedReportName(modifyReport.name);
      const selectedFields = modifyReport.report_fields.map((item) => {
        const field = dataReportAllFields.results.find(
          (field) => field.identifier === item.identifier
        ) as StandardReportFieldResponse;
        return {
          id: field?.id.toString(),
          title: field?.name,
        };
      });
      setFieldValue("fields", selectedFields);
      setSelectedFields(selectedFields);
      setRightListData(selectedFields);
      setModifyReportDataReady(true);
      if (APP_CONFIG.DEBUG.ALL) console.log("modifyReportDataReady: true");
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "modifyReportDataReady: false",
          !!modifyReport,
          dataReportAllFields,
          isFetchingReportAllFields,
          selectedReportId
        );
    }
  }, [modifyReport, dataReportAllFields, isFetchingReportAllFields]);

  useEffect(() => {
    formik.validateForm();
  }, []);

  const hideModal = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showModalNewCustomReportStep1: false,
        showModalNewCustomReportStep2: false,
        showModalNewCustomReportStep3: false,
      })
    );
    resetCreateCustomReportForms();
  };

  return (
    <form
      className="grid grid-cols-6 gap-6 fixed z-overlay"
      onSubmit={handleSubmit}
    >
      {/* keeping FTModal inside form tag to wrap report_name field inside */}
      <FTModal
        visible={modalsState.showModalNewCustomReportStep2}
        containerStyle="max-w-[calc(100vw-4rem)] w-[903px]"
        // CustomHeadingWrapper={(Heading) => (
        //   <div className="flex-grow text-2xl font-bold text-heading-black">
        //     {Heading}
        //   </div>
        // )}
        dismissible={true}
        heading={
          isCopyModify
            ? t("heading_copy", { name: modifyReport?.name })
            : t(modifyReport ? "heading_modify" : "heading")
        }
        // heading={(
        //   <div className="flex flex-col gap-10">
        //     <div className="grid grid-cols-12">
        //       <AdminFormFieldInput
        //         label={tFormFields('report_name')}
        //         placeholder={tFormFields('report_name_placeholder')}
        //         type="text"
        //         id="report_name"
        //         name="report_name"
        //         value={values.report_name}
        //         onChange={handleChange}
        //         onBlur={handleBlur}
        //         touched={touched.report_name}
        //         error={errors.report_name}
        //         // detailsFormField={true}
        //         customWrapperClass="col-span-6 font-normal"
        //         customInputClass=""
        //       />
        //     </div>
        //     <div>{"Select Columns"}</div>
        //   </div>
        // )}
        onHide={hideModal}
        position="center"
      >
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute top-5 right-36 text-[8px] w-72 text-wrap">
            selectedReportId: {selectedReportId}
            <br />
            selectedFields: <br />
            {selectedFields.map((item, i) => (
              <span key={i}>
                {JSON.stringify(item)}
                <br />
              </span>
            ))}
            ==================
            <br />
            values: <br />
            {JSON.stringify(values)}
            <br />
            ==================
            <br />
            errors: <br />
            {JSON.stringify(errors)}
            <br />
            ==================
            <br />
            values.fields: <br />
            {values.fields.map((item, i) => (
              <span key={i}>
                {JSON.stringify(item)}
                <br />
              </span>
            ))}
          </pre>
        )}
        <div className="flex flex-col">
          {/* <pre className="text-[8px]">{JSON.stringify(selectedFields, null, 2)}</pre>
          <pre className="text-[8px]">{JSON.stringify(values, null, 2)}</pre>
          <pre className="text-[8px]">{JSON.stringify(errors, null, 2)}</pre> */}

          <div className="flex flex-col gap-10">
            <div className="grid grid-cols-12">
              <AdminFormFieldInput
                ref={firstInputRef}
                label={tFormFields("report_name")}
                placeholder={tFormFields("report_name_placeholder")}
                type="text"
                id="report_name"
                name="report_name"
                value={values.report_name}
                onChange={handleChange}
                // onBlur={handleBlur} // prevent trigger of same API multiple times, onChange is enough
                touched={touched.report_name}
                error={errors.report_name}
                // detailsFormField={true}
                customWrapperClass="col-span-6 font-normal"
                customInputClass=""
                showProcessingIndicator={isFetchingReportsByCatType}
              />
            </div>
            <h3 className="text-xl font-bold text-heading-black mb-2">
              {t("sub_heading")}
            </h3>
          </div>

          {/* Sub Header */}
          <div className="mb-4 text-heading-gray888">{t("description")}</div>

          <div className="col-span-6 grid grid-cols-12 gap-6">
            {isFetchingReportAllFields || !modifyReportDataReady ? (
              <div className="col-span-12 flex justify-center items-center h-96">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="loading"
                  className="w-12 h-12"
                />
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                  <pre className="text-[8px]">
                    isFetchingReportAllFields:{" "}
                    {JSON.stringify(isFetchingReportAllFields)}
                    <br />
                    modifyReportDataReady:{" "}
                    {JSON.stringify(modifyReportDataReady)}
                    <br />
                  </pre>
                )}
              </div>
            ) : (
              <DualListbox
                leftTitle={t("available_w_count", {
                  count:
                    (dataReportAllFields?.count ?? 0) - values.fields.length,
                })}
                leftSearchPlaceholder={t("search_placeholder")}
                leftList={
                  dataReportAllFields?.results
                    ?.filter(
                      (item) =>
                        !rightListData.some(
                          (field) => field.id === item.id.toString()
                        )
                    )
                    ?.map(
                      (item) =>
                        ({
                          id: item.id.toString(),
                          title: item.name,
                        }) as TCustomizableItemList
                    ) || []
                }
                rightTitle={t("added_w_count", {
                  count: selectedFields.length,
                })}
                rightSearchPlaceholder={t("search_placeholder")}
                rightList={
                  selectedFields?.length > 0 ? selectedFields : rightListData
                } // Show already selected fields if coming back from report options
                rightSelectCallback={(newSelection) => {
                  setSelectedFields(newSelection); // added to ensure that the RHS count updates with changes
                  setFieldValue("fields", newSelection);
                  setTimeout(() => {
                    setFieldTouched("fields", true);
                  }, 100); // To show error message
                }}
                handleCancelButton={hideModal}
                handleSaveButton={() => {
                  formik.submitForm();
                }}
                saveButtonText={t(
                  isCopyModify
                    ? "create_copy"
                    : modifyReport
                      ? "modify_report"
                      : "save_and_continue"
                )}
                saveButtonEnabled={formik.isValid}
                rightSortable={true}
                rightSortExcludedItems={(selectedFields?.length > 0
                  ? selectedFields
                  : rightListData
                )
                  // items that have group by enabled
                  ?.filter((item) => {
                    const this_report_field =
                      specificReportResponse?.report_fields?.find(
                        (field) =>
                          Number(field.field.all_field_id) === Number(item.id)
                      );
                    return !!this_report_field?.is_group_by;
                  })}
                key={`dual-listbox-${selectedReportId}-${modifyReport?.id}-${JSON.stringify(isFetchingReportAllFields)}`} // refresh when report changes
                disabledSubmit={
                  isSubmitting ||
                  isFetchingReportAllFields ||
                  isFetchingReportsByCatType
                } // to disable the submit button when async form validation runs
              />
            )}
          </div>

          {!isFetchingReportAllFields &&
            errors?.fields &&
            typeof errors?.fields === "string" && (
              <p
                className={`col-span-12 bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-4`}
              >
                {errors.fields}
              </p>
            )}
        </div>
      </FTModal>
    </form>
  );
};

export default ModalCreateCustomReportsStep2;
